import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from "./index";

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Fetch paginated notification recipients (replaces centro-notifiche/list)
 * @param {Object} params
 * @param {number} params.page - Page number (default 1)
 * @param {number} params.limit - Items per page (default 20)
 * @param {string} params.order - "asc" | "desc" (default "desc")
 * @param {string} params.scope - "all" | "unread" (optional)
 * @param {number} params.topic - Topic ID filter (optional)
 */
export const fetchRecipients = createAsyncThunk(
  "notificationRecipients/fetchRecipients",
  async (
    { page = 1, limit = 20, order = "desc", scope, topic } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        order,
        page: String(page),
        limit: String(limit),
      });
      if (scope) params.set("scope", scope);
      if (topic) params.set("topic", String(topic));

      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients/feed?${params}`,
        { headers: headersDefault() }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(json?.message ?? "Errore caricamento notifiche");
      }
      return { data: json?.data ?? [], meta: json?.meta ?? {}, page };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Fetch unread count only (lightweight call for badge)
 * Uses the unified unread count endpoint.
 */
export const fetchUnreadCount = createAsyncThunk(
  "notificationRecipients/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients/feed/unread/count`,
        { headers: headersDefault() }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(json?.message ?? "Errore conteggio notifiche");
      }
      return { total: json?.unread_count ?? 0 };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Mark recipients as read.
 *
 * Used for marking individual notifications when the user taps a row.
 * We deliberately do NOT mark everything as read on page entry — a
 * notification's `read_at` is set only when the user actually opens it.
 * The bell badge stays accurate via the `notifications_last_opened_at`
 * watermark, which the backend advances on every GET /feed call
 * (see ListFeedHandler) and which is queried by fetchUnreadCount() in
 * Notifications/index.js's `useIonViewDidLeave` hook.
 *
 * @param {number[]} recipientIds - Array of recipient IDs
 */
export const markRecipientsRead = createAsyncThunk(
  "notificationRecipients/markRead",
  async (recipientIds, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients/read`,
        {
          method: "PATCH",
          headers: headersDefault(),
          body: JSON.stringify({ recipients: recipientIds }),
        }
      );
      if (!res.ok) {
        const json = await safeJson(res);
        return rejectWithValue(
          json?.message ?? "Errore lettura notifica"
        );
      }
      return { recipientIds };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Mark recipients as read using deliver_id (from push notification payload)
 * @param {string[]} deliveryIds - Array of deliver_id strings
 */
export const markRecipientsReadByDelivery = createAsyncThunk(
  "notificationRecipients/markReadByDelivery",
  async (deliveryIds, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients/read`,
        {
          method: "PATCH",
          headers: headersDefault(),
          body: JSON.stringify({ deliveries: deliveryIds }),
        }
      );
      if (!res.ok) {
        const json = await safeJson(res);
        return rejectWithValue(json?.message ?? "Errore lettura notifica");
      }
      return { deliveryIds };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Fetch legacy unread notification IDs from centro-notifiche
 */
export const fetchLegacyUnreadIds = createAsyncThunk(
  "notificationRecipients/fetchLegacyUnreadIds",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/centro-notifiche/list?filter=unread&fullLayout=false&app=true`,
        { headers: headersDefault() }
      );
      const json = await safeJson(res);
      if (!res.ok) {
        return rejectWithValue(json?.message ?? "Errore caricamento notifiche legacy");
      }
      const notifications = json?.notifications?.data ?? [];
      const ids = notifications.map((n) => n.id).filter(Boolean);
      return { ids };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Mark legacy notification-receiver as read using old centro-notifiche API
 * @param {number} notificationId - Legacy notification ID
 */
export const markLegacyNotificationRead = createAsyncThunk(
  "notificationRecipients/markLegacyRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/centro-notifiche/${notificationId}/fetch?app=true`,
        { headers: headersDefault() }
      );
      if (!res.ok) return rejectWithValue("Errore lettura notifica");
      return { id: notificationId };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Mark recipients as unread
 * @param {number[]} recipientIds - Array of recipient IDs
 */
export const markRecipientsUnread = createAsyncThunk(
  "notificationRecipients/markUnread",
  async (recipientIds, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients/unread`,
        {
          method: "PATCH",
          headers: headersDefault(),
          body: JSON.stringify({ recipients: recipientIds }),
        }
      );
      if (!res.ok) {
        const json = await safeJson(res);
        return rejectWithValue(
          json?.message ?? "Errore aggiornamento notifica"
        );
      }
      return { recipientIds };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);

/**
 * Delete recipients
 * @param {number[]} recipientIds - Array of recipient IDs
 */
export const deleteRecipients = createAsyncThunk(
  "notificationRecipients/delete",
  async (recipientIds, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${baseUrl}/api/v1/push-notifications/recipients`,
        {
          method: "DELETE",
          headers: headersDefault(),
          body: JSON.stringify({ recipients: recipientIds }),
        }
      );
      if (!res.ok) {
        const json = await safeJson(res);
        return rejectWithValue(
          json?.message ?? "Errore eliminazione notifica"
        );
      }
      return { recipientIds };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);


